import type {
  AnyEntry,
  ContactField,
  ContactInfo,
  ContactLink,
  EducationEntry,
  EntryPatch,
  EntrySection,
  ExperienceEntry,
  FactState,
  MasterProfile,
  Period,
  ProfileEntries,
  ProjectEntry,
  CertificationEntry,
  SkillEntry,
  TrackedValue,
} from './types';

/**
 * Ids are a session-local counter. Nothing here needs to survive a reload —
 * the profile lives in memory only — and fixed ids keep tests readable.
 */
let idCounter = 0;

export function nextId(prefix: string): string {
  idCounter += 1;
  return `${prefix}-${idCounter.toString(36)}`;
}

export function blankPeriod(): Period {
  return { start: '', end: '', current: false, raw: '' };
}

export function blankExperience(): ExperienceEntry {
  return {
    id: nextId('exp'),
    organization: '',
    role: '',
    location: '',
    period: blankPeriod(),
    responsibilities: [],
    achievements: [],
    state: 'needs-review',
    evidence: '',
  };
}

export function blankEducation(): EducationEntry {
  return {
    id: nextId('edu'),
    institution: '',
    credential: '',
    area: '',
    period: blankPeriod(),
    state: 'needs-review',
    evidence: '',
  };
}

export function blankSkill(): SkillEntry {
  return { id: nextId('skill'), name: '', state: 'needs-review', evidence: '' };
}

export function blankCertification(): CertificationEntry {
  return {
    id: nextId('cert'),
    name: '',
    issuer: '',
    date: '',
    state: 'needs-review',
    evidence: '',
  };
}

export function blankProject(): ProjectEntry {
  return {
    id: nextId('proj'),
    name: '',
    url: '',
    description: '',
    period: blankPeriod(),
    state: 'needs-review',
    evidence: '',
  };
}

export function blankContactLink(): ContactLink {
  return { id: nextId('link'), label: '', url: '', state: 'needs-review', evidence: '' };
}

function blankTracked(): TrackedValue {
  return { value: '', state: 'needs-review' };
}

export function emptyContact(): ContactInfo {
  return {
    fullName: blankTracked(),
    email: blankTracked(),
    phone: blankTracked(),
    location: blankTracked(),
    links: [],
  };
}

export function emptyProfile(): MasterProfile {
  return {
    contact: emptyContact(),
    summary: blankTracked(),
    experience: [],
    education: [],
    skills: [],
    certifications: [],
    projects: [],
  };
}

/**
 * Text the user typed is their own statement, but an empty or freshly changed
 * fact is not confirmed yet: every edit demotes the fact so an earlier
 * confirmation cannot outlive the content it covered.
 */
export function trackedWithValue(current: TrackedValue, value: string): TrackedValue {
  if (current.value === value) return current;
  return { value, state: 'corrected' };
}

/** An accepted suggestion is a quote from a document, not a confirmed fact. */
export function importedTracked(current: TrackedValue, value: string): TrackedValue {
  if (current.value === value) return current;
  return { value, state: 'needs-review' };
}

export function isVerified(state: FactState): boolean {
  return state === 'confirmed';
}

export function entryList<K extends EntrySection>(
  profile: MasterProfile,
  section: K,
): ProfileEntries[K][] {
  // The generic key is deferred here, so the read needs one explicit cast.
  return profile[section] as ProfileEntries[K][];
}

/**
 * Replaces one section's list. The cast is safe because `list` is always read
 * back from `profile[section]` by the callers below, so its element type matches
 * the key.
 */
function withEntryList(
  profile: MasterProfile,
  section: EntrySection,
  list: AnyEntry[],
): MasterProfile {
  return { ...profile, [section]: list } as MasterProfile;
}

function findIndex<T extends { id: string }>(list: readonly T[], id: string): number {
  return list.findIndex((entry) => entry.id === id);
}

export function appendEntry<K extends EntrySection>(
  profile: MasterProfile,
  section: K,
  entry: ProfileEntries[K],
): MasterProfile {
  return withEntryList(profile, section, [...entryList(profile, section), entry]);
}

export function removeEntry(
  profile: MasterProfile,
  section: EntrySection,
  id: string,
): MasterProfile {
  const entries = entryList(profile, section);
  const index = findIndex(entries, id);
  if (index < 0) return profile;
  return withEntryList(
    profile,
    section,
    entries.filter((entry) => entry.id !== id),
  );
}

export function updateEntry<K extends EntrySection>(
  profile: MasterProfile,
  section: K,
  id: string,
  patch: EntryPatch<K>,
): MasterProfile {
  const entries: ProfileEntries[K][] = entryList(profile, section);
  return withEntryList(
    profile,
    section,
    entries.map((entry) =>
      entry.id === id ? { ...entry, ...patch, state: 'corrected' } : entry,
    ) as AnyEntry[],
  );
}

export function confirmEntry(
  profile: MasterProfile,
  section: EntrySection,
  id: string,
): MasterProfile {
  const entries: AnyEntry[] = entryList(profile, section);
  return withEntryList(
    profile,
    section,
    entries.map((entry) => (entry.id === id ? { ...entry, state: 'confirmed' } : entry)),
  );
}

/** Replaces an existing entry's imported content in place, without granting
 *  confirmation. Used only after an explicit user decision to replace. */
export function updateEntryAsImport<K extends EntrySection>(
  profile: MasterProfile,
  section: K,
  id: string,
  patch: EntryPatch<K>,
): MasterProfile {
  const entries: ProfileEntries[K][] = entryList(profile, section);
  return withEntryList(
    profile,
    section,
    entries.map((entry) =>
      entry.id === id ? { ...entry, ...patch, state: 'needs-review' } : entry,
    ) as AnyEntry[],
  );
}

/** Swaps an entry with its neighbour. Returns the profile unchanged at the edges. */
export function moveEntry(
  profile: MasterProfile,
  section: EntrySection,
  id: string,
  delta: -1 | 1,
): MasterProfile {
  const entries: AnyEntry[] = entryList(profile, section);
  const from = findIndex(entries, id);
  const to = from + delta;
  if (from < 0 || to < 0 || to >= entries.length) return profile;
  const next = [...entries];
  const moved = next[from];
  const target = next[to];
  if (!moved || !target) return profile;
  next[from] = target;
  next[to] = moved;
  return withEntryList(profile, section, next);
}

export function setContactField(
  profile: MasterProfile,
  field: ContactField,
  value: string,
): MasterProfile {
  return {
    ...profile,
    contact: {
      ...profile.contact,
      [field]: trackedWithValue(profile.contact[field], value),
    },
  };
}

export function setContactFieldAsImport(
  profile: MasterProfile,
  field: ContactField,
  value: string,
): MasterProfile {
  return {
    ...profile,
    contact: {
      ...profile.contact,
      [field]: importedTracked(profile.contact[field], value),
    },
  };
}

export function confirmContactField(
  profile: MasterProfile,
  field: ContactField,
): MasterProfile {
  const current = profile.contact[field];
  return {
    ...profile,
    contact: { ...profile.contact, [field]: { ...current, state: 'confirmed' } },
  };
}

export function setSummary(
  profile: MasterProfile,
  value: string,
  imported = false,
): MasterProfile {
  const current = profile.summary;
  if (current.value === value) return profile;
  return {
    ...profile,
    summary: { value, state: imported ? 'needs-review' : 'corrected' },
  };
}

export function confirmSummary(profile: MasterProfile): MasterProfile {
  return { ...profile, summary: { ...profile.summary, state: 'confirmed' } };
}

function withContactLinks(
  profile: MasterProfile,
  links: ContactLink[],
): MasterProfile {
  return { ...profile, contact: { ...profile.contact, links } };
}

export function addContactLink(profile: MasterProfile): MasterProfile {
  return withContactLinks(profile, [...profile.contact.links, blankContactLink()]);
}

export function updateContactLink(
  profile: MasterProfile,
  id: string,
  patch: Partial<Omit<ContactLink, 'id' | 'state' | 'evidence'>>,
): MasterProfile {
  return withContactLinks(
    profile,
    profile.contact.links.map((link) =>
      link.id === id ? { ...link, ...patch, state: 'corrected' } : link,
    ),
  );
}

export function updateContactLinkAsImport(
  profile: MasterProfile,
  id: string,
  patch: Partial<Omit<ContactLink, 'id' | 'state' | 'evidence'>>,
): MasterProfile {
  return withContactLinks(
    profile,
    profile.contact.links.map((link) =>
      link.id === id ? { ...link, ...patch, state: 'needs-review' } : link,
    ),
  );
}

export function confirmContactLink(profile: MasterProfile, id: string): MasterProfile {
  return withContactLinks(
    profile,
    profile.contact.links.map((link) =>
      link.id === id ? { ...link, state: 'confirmed' } : link,
    ),
  );
}

export function removeContactLink(profile: MasterProfile, id: string): MasterProfile {
  return withContactLinks(
    profile,
    profile.contact.links.filter((link) => link.id !== id),
  );
}

export function moveContactLink(
  profile: MasterProfile,
  id: string,
  delta: -1 | 1,
): MasterProfile {
  const links = profile.contact.links;
  const from = findIndex(links, id);
  const to = from + delta;
  if (from < 0 || to < 0 || to >= links.length) return profile;
  const next = [...links];
  const moved = next[from];
  const target = next[to];
  if (!moved || !target) return profile;
  next[from] = target;
  next[to] = moved;
  return withContactLinks(profile, next);
}

export type FactCensus = {
  total: number;
  needsReview: number;
  corrected: number;
  confirmed: number;
};

function countStates(
  entries: readonly { state: FactState }[],
  census: FactCensus,
): void {
  for (const entry of entries) {
    census.total += 1;
    if (entry.state === 'confirmed') census.confirmed += 1;
    else if (entry.state === 'corrected') census.corrected += 1;
    else census.needsReview += 1;
  }
}

/** Counts every individual fact, so "confirmed" never overstates coverage. */
export function census(profile: MasterProfile): FactCensus {
  const result: FactCensus = { total: 0, needsReview: 0, corrected: 0, confirmed: 0 };
  for (const field of ['fullName', 'email', 'phone', 'location'] as const) {
    result.total += 1;
    const state = profile.contact[field].state;
    if (state === 'confirmed') result.confirmed += 1;
    else if (state === 'corrected') result.corrected += 1;
    else result.needsReview += 1;
  }
  result.total += 1;
  if (profile.summary.state === 'confirmed') result.confirmed += 1;
  else if (profile.summary.state === 'corrected') result.corrected += 1;
  else result.needsReview += 1;
  countStates(profile.contact.links, result);
  countStates(profile.experience, result);
  countStates(profile.education, result);
  countStates(profile.skills, result);
  countStates(profile.certifications, result);
  countStates(profile.projects, result);
  return result;
}

export function normalizeFact(text: string): string {
  return text.trim().toLowerCase().replace(/\s+/g, ' ');
}

/** Identity used to spot a suggestion that duplicates something already stored. */
export function entrySignature(entry: AnyEntry): string {
  if ('organization' in entry) {
    return `${normalizeFact(entry.organization)}|${normalizeFact(entry.role)}`;
  }
  if ('institution' in entry) {
    return `${normalizeFact(entry.institution)}|${normalizeFact(entry.credential)}`;
  }
  return normalizeFact(entry.name);
}

export function linkSignature(link: ContactLink): string {
  return normalizeFact(link.url);
}
